/**
 * MONSTER TOWN — TOASTS + FLYING PICTURES. HUD/AUDIO/TOUCH CREW (src/ui/**).
 *
 * Two jobs:
 *  1. `ui:toast` — a picture badge that appears, waits, and leaves. Pictures
 *     only; the payload's `icon` is an icon name, never a word.
 *  2. Flying rewards — when coins are earned at a world position, a little coin
 *     picture flies up from THAT SPOT toward the purse, so the kid sees where
 *     the money came from. This is the whole feedback loop of the economy.
 *
 * THE HARD RULE APPLIES HERE TOO: world -> screen goes through
 * `worldToCanvasPx()`, which reads `canvas.getBoundingClientRect()`. Never
 * window.innerWidth/Height. On an older iPad those disagree and every coin
 * lands in the wrong place.
 */

import { bus } from '../core/events'
import { worldToCanvasPx } from '../input/pick'
import type { Vec3 } from '../core/types'
import type { GameContext } from '../app/context'
import { el } from './dom'
import { iconEl } from './icons'

const MAX_TOASTS = 3
/** Hard cap on simultaneous flying pictures — an older iPad and a lot of DOM. */
const MAX_FLY = 10

let flying = 0

export function installToasts(ctx: GameContext, hud: HTMLElement): void {
  const rail = el('div', 'mt-toasts')
  hud.append(rail)

  const flyLayer = el('div', '')
  flyLayer.style.position = 'absolute'
  flyLayer.style.inset = '0'
  flyLayer.style.pointerEvents = 'none'
  flyLayer.style.overflow = 'hidden'
  hud.append(flyLayer)

  bus.on('ui:toast', (e) => {
    while (rail.childElementCount >= MAX_TOASTS) rail.firstElementChild?.remove()
    const t = el('div', 'mt-toast')
    t.append(iconEl(e.icon, 52))
    rail.append(t)
    window.setTimeout(() => t.remove(), Math.max(600, e.ms ?? 1600))
  })

  /**
   * "LOOK WHAT HAPPENED WHILE YOU WERE GONE."
   *
   * The town-life crew has always worked out the away report — presents on the
   * doorstep, a new monster who moved in — and emitted it. Nothing listened, so
   * a kid came back to a gift box sitting somewhere in a 24x24 town with no idea
   * it was there. The brief calls this the retention engine; it was running with
   * the output pipe disconnected.
   *
   * Deliberately a picture, held long, and nothing to dismiss (LAW #4, and no
   * dead ends): a heart for a present, a monster for a new neighbour. The gift
   * itself is a real tappable thing out in the world — this is the pointer.
   */
  bus.on('townlife:awayReport', (e) => {
    if (e.gifts <= 0 && e.newNeighbours <= 0) return
    if (e.gifts > 0) bus.emit('ui:toast', { icon: 'heart', ms: 4200 })
    if (e.newNeighbours > 0) bus.emit('ui:toast', { icon: 'monster', ms: 4200 })
    bus.emit('audio:cue', { cue: 'chime_happy' })
  })

  const fly = (icon: string, at: Vec3, amount?: number): void => {
    if (flying >= MAX_FLY) return
    const p = worldToCanvasPx(ctx.camera.camera, ctx.canvas, at)
    if (p.behind) return
    const node = el('div', 'mt-fly')
    node.style.left = `${p.x}px`
    node.style.top = `${p.y}px`
    node.append(iconEl(icon, 44))
    if (amount !== undefined && amount > 0) {
      const n = el('span', 'mt-num')
      n.textContent = `+${amount}`
      node.append(n)
    }
    flyLayer.append(node)
    flying++
    window.setTimeout(() => {
      node.remove()
      flying = Math.max(0, flying - 1)
    }, 950)
  }

  bus.on('ui:fly', (e) => fly(e.icon, e.at, e.amount))

  // Coins earned anywhere in the world throw a coin picture from that spot.
  bus.on('economy:earned', (e) => {
    if (e.at === undefined) return
    fly('coin', e.at, e.amount)
  })

  // Unlocks: a star burst from wherever the monster is standing, plus a badge.
  bus.on('economy:unlocked', () => {
    bus.emit('ui:toast', { icon: 'star', ms: 2200 })
  })
}
